import {t} from '@github-ui/swp-core/lib/utils/localization'
import {
  CodescanIcon,
  CodespacesIcon,
  CodeSquareIcon,
  CommentDiscussionIcon,
  CopilotIcon,
  MarkGithubIcon,
  MegaphoneIcon,
  StackIcon,
  ShieldCheckIcon,
  ShieldLockIcon,
  WorkflowIcon,
} from '@primer/octicons-react'

import platformImage from './assets/platform.png'
import platformDarkImage from './assets/platform-dark.png'
import copilotImage from './assets/copilot.png'
import copilotDarkImage from './assets/copilot-dark.png'
import changelogImage from './assets/changelog.png'
import changelogDarkImage from './assets/changelog-dark.png'
import universeImage from './assets/universe.png'

import type {NavDropdownType} from './components/NavDropdown/NavDropdown'
import type {NavLinkType} from './components/NavLink/NavLink'

export function getMarketingNavigationIaData(redesign = false): Array<NavDropdownType | NavLinkType> {
  const copilot: NavLinkType = {
    title: t('Copilot'),
    subtitle: t('AI coding agent for tasks, projects and automated work'),
    url: '/features/copilot',
    icon: CopilotIcon,
    image: redesign ? {light: copilotImage, dark: copilotDarkImage} : undefined,
  }
  const codespaces: NavLinkType = {
    title: t('Codespaces'),
    subtitle: t('Cloud development environments'),
    url: '/features/codespaces',
    icon: CodespacesIcon,
  }

  return [
    {
      title: t('Products'),
      groups: [
        {
          title: t('Platform'),
          hideTitle: true,
          featured: redesign,
          items: [
            {
              title: t('GitHub'),
              subtitle: t('Repository hosting, code review, and collaboration'),
              url: redesign ? '/platform' : 'https://gh.io/platform',
              icon: redesign ? StackIcon : MarkGithubIcon,
              image: redesign ? {light: platformImage, dark: platformDarkImage} : undefined,
            },
            copilot,
          ],
        },
        {
          title: t('Build & Ship'),
          items: [
            {
              title: t('Actions'),
              subtitle: t('CI/CD and automation'),
              url: '/features/actions',
              icon: WorkflowIcon,
            },
            {
              title: t('Code Quality'),
              subtitle: t('Quality analysis, fixes, and enforcement'),
              url: '/features/code-quality',
              icon: CodescanIcon,
            },
            codespaces,
          ],
        },
        {
          title: t('Secure'),
          items: [
            {
              title: t('Code Security'),
              subtitle: t('Vulnerability detection and remediation'),
              url: '/security/advanced-security/code-security',
              icon: CodeSquareIcon,
            },
            {
              title: t('Secret Protection'),
              subtitle: t('Secret scanning and leak prevention'),
              url: '/security/advanced-security/secret-protection',
              icon: ShieldLockIcon,
            },
          ],
        },
      ],
      trailingLink: {title: t('All Features'), url: '/features'},
    },
    {
      title: t('Developers'),
      groups: [
        {
          title: t('Learn'),
          items: [
            {title: t('Documentation'), url: 'https://docs.github.com/en', external: true},
            {title: t('Training'), url: 'https://learn.github.com/skills', external: true},
            {title: t('Certifications'), url: 'https://learn.github.com/credentials', external: true},
          ],
        },
        {
          title: t('Build'),
          items: [
            {title: t('Build with Copilot'), url: '/features/ai'},
            {
              title: t('Customize Copilot'),
              url: 'https://docs.github.com/en/copilot/how-tos/copilot-on-github/customize-copilot/customize-copilot-overview',
              external: true,
            },
            {title: t('Integrations'), url: '/marketplace'},
          ],
        },
        {
          title: t('Explore'),
          items: [
            {title: redesign ? t('Trending repos') : t('Trending Repos'), url: '/trending'},
            {title: t('GitHub Sponsors'), url: '/open-source/sponsors'},
            {title: t('Events'), url: '/resources/events'},
          ],
        },
        ...(redesign
          ? [
              {
                title: t('Changelog'),
                hideTitle: true,
                featured: true,
                items: [
                  {
                    title: t('Changelog'),
                    subtitle: t(
                      'Your source for the latest features, improvements, and fixes across GitHub products, all in one place.',
                    ),
                    url: 'https://github.blog/changelog/',
                    external: true,
                    icon: MegaphoneIcon,
                    image: {light: changelogImage, dark: changelogDarkImage},
                  },
                ],
              },
            ]
          : []),
      ],
    },
    {
      title: t('Businesses'),
      groups: [
        {
          title: t('By Stage'),
          items: [
            {title: t('Enterprises'), url: '/enterprise'},
            {title: redesign ? t('Team') : t('Small & medium teams'), url: '/team'},
            {title: t('Startups'), url: '/enterprise/startups'},
            {title: t('Nonprofits'), url: '/solutions/industry/nonprofits'},
          ],
        },
        {
          title: t('By Industry'),
          items: [
            {title: t('Healthcare'), url: '/solutions/industry/healthcare'},
            {title: t('Financial services'), url: '/solutions/industry/financial-services'},
            {title: t('Manufacturing'), url: '/solutions/industry/manufacturing'},
            {title: t('Government'), url: '/solutions/industry/government'},
            {
              title: t('See all industries'),
              url: '/solutions/industry',
              withArrow: true,
            },
          ],
        },
        {
          title: t('Evaluate GitHub'),
          items: [
            {title: t('Why GitHub'), url: '/why-github'},
            {title: redesign ? t('Customer Stories') : t('Customer stories'), url: '/customer-stories'},
            {title: t('Business insights'), url: '/solutions/executive-insights'},
            {title: t('Trust Center'), url: '/trust-center'},
          ],
        },
        {
          title: t('Add-ons'),
          hasSeparator: redesign,
          items: [
            {
              title: t('GitHub Advanced Security'),
              url: '/security/advanced-security',
              icon: ShieldCheckIcon,
              subtitle: redesign ? t('Enterprise-grade code security') : undefined,
            },
            {
              title: redesign ? t('Copilot for business') : t('Copilot for Business'),
              url: '/features/copilot/copilot-business',
              icon: CopilotIcon,
              subtitle: redesign ? t('Enterprise-grade AI features') : undefined,
            },
            {
              title: t('Premium Support'),
              url: '/enterprise/premium-support',
              icon: CommentDiscussionIcon,
              subtitle: redesign ? t('Enterprise-grade 24/7 support') : undefined,
            },
          ],
        },
      ],
    },
    {
      title: t('Resources'),
      groups: [
        {
          title: t('Learn & Connect'),
          items: [
            {title: t('Documentation'), url: 'https://docs.github.com/en', external: true},
            {title: redesign ? t('Resource center') : t('Resource Center'), url: '/resources'},
            {title: t('Blog'), url: 'https://github.blog/', external: true},
            {title: t('Events & webinars'), url: '/resources/events'},
            {title: redesign ? t('Community Forum') : t('Community forum'), url: '/orgs/community/discussions'},
            {title: redesign ? t('Customer Stories') : t('Customer stories'), url: '/customer-stories'},
            {title: t('Integrations'), url: '/marketplace'},
          ],
        },
        {
          title: t('Support'),
          items: [
            {
              title: redesign ? t('Customer support') : t('Customer Support'),
              url: 'https://help.github.com/',
              external: true,
            },
            {title: t('Training'), url: 'https://learn.github.com/skills', external: true},
            {title: t('GitHub Status'), url: 'https://www.githubstatus.com/', external: true},
          ],
        },
        {
          title: t('Company'),
          items: [
            {title: redesign ? t('About') : t('About GitHub'), url: '/about'},
            {title: t('Partners'), url: '/partners'},
            {title: t('Changelog'), url: 'https://github.blog/changelog/', external: true},
          ],
        },
        ...(redesign
          ? [
              {
                title: t('GitHub Universe'),
                hideTitle: true,
                featured: true,
                items: [
                  {
                    title: t('GitHub Universe'),
                    subtitle: t(
                      'The event for developers, by developers — talks, workshops, and what’s next for GitHub.',
                    ),
                    url: 'https://githubuniverse.com/',
                    external: true,
                    icon: MarkGithubIcon,
                    image: {light: universeImage, dark: universeImage},
                  },
                ],
              },
            ]
          : []),
      ],
    },
    {title: t('Pricing'), url: '/pricing'},
  ]
}
